import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FlaskConical,
  Loader2,
  Play,
  Plus,
  Trash2,
  CheckCircle2,
  Circle,
  XCircle,
  RotateCcw,
  PenLine,
  Square,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import apiClient from '../api/client';
import { readSseEvents, type SseEvent } from '../api/sse';
import { useToast } from '../components/ui/toastContext';

// ---------------------------------------------------------------------------
// 类型（与 backend/app/validation/report.py build_report 输出对齐）
// ---------------------------------------------------------------------------
interface ArtifactDetail {
  id: string;
  kind: string;
  name: string;
  version: string;
  manifest?: {
    validation_scenarios?: { id?: string; question: string; history?: unknown }[];
  } | null;
}

interface ScenarioRow {
  id: string;
  question: string;
  history: string; // 可选：一行 JSON 文本（消息数组）
}

interface ReportScenario {
  scenario_id: string;
  question: string;
  success: boolean;
  skill_triggered: boolean;
  reference_reads: number;
  citation_hits: number;
  tool_calls: number;
  tool_failures: number;
  refused: boolean;
  final_excerpt: string;
}

interface VerifyReport {
  pass_rate: number;
  scenarios_total: number;
  scenarios_passed: number;
  dimensions: {
    trigger: { scenarios_triggered: number; rate: number };
    reference: { reference_reads: number; scenarios_with_citation: number; citation_markers: number };
    tool: { calls: number; succeeded: number; failed: number; success_rate: number | null };
    boundary: { rejected: number; scenarios_rejected: number };
  };
  weaknesses: { type: string; scenario_id: string; detail: string }[];
  scenarios: ReportScenario[];
}

const KIND_LABELS: Record<string, string> = {
  skill: '技能（Skill）',
  mcp: 'MCP 工具',
  kb: '知识库（KB）',
  preset: '能力预设',
};

const WEAKNESS_LABELS: Record<string, string> = {
  missing_knowledge: '缺失知识',
  trigger_inaccurate: '触发不准',
  tool_failure: '工具失败',
  boundary_not_rejected: '越界未拒绝',
  scenario_failed: '场景失败',
};

const STAGES = [
  { key: 'loaded', label: '加载产物' },
  { key: 'scenarios', label: '执行验证场景' },
  { key: 'persisted', label: '事件落库' },
  { key: 'reported', label: '生成报告' },
];

const kindLabel = (kind: string) => KIND_LABELS[kind] || kind;
const weaknessLabel = (type: string) => WEAKNESS_LABELS[type] || type;

// 提取后端错误文案：优先 response.data.detail（字符串），其次 Error.message
const apiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return typeof err.message === 'string' ? err.message : '';
};

/** 按产物类型给出一个预填示例场景。 */
const sampleScenario = (kind: string): ScenarioRow => {
  const samples: Record<string, string> = {
    skill: '这个技能能解决什么问题？请演示一次典型用法。',
    mcp: '请调用工具完成一次典型操作，并说明返回结果。',
    kb: '知识库中关于核心主题的要点是什么？请引用资料回答。',
  };
  return { id: '', question: samples[kind] || '请介绍这个产物的能力，并回答一个典型问题。', history: '' };
};

const emptyScenario = (): ScenarioRow => ({ id: '', question: '', history: '' });

export default function ArtifactPlayground() {
  const toast = useToast();
  const navigate = useNavigate();
  const { artifactId: routeArtifactId } = useParams<{ artifactId: string }>();

  const [idInput, setIdInput] = useState(routeArtifactId || '');
  const [artifact, setArtifact] = useState<ArtifactDetail | null>(null);
  const [loadingArtifact, setLoadingArtifact] = useState(false);

  const [scenarios, setScenarios] = useState<ScenarioRow[]>([emptyScenario()]);
  const [useBuiltin, setUseBuiltin] = useState(false);

  const [running, setRunning] = useState(false);
  const [doneEvents, setDoneEvents] = useState<string[]>([]);
  const [scenarioDone, setScenarioDone] = useState(0);
  const [scenarioTotal, setScenarioTotal] = useState<number | null>(null);
  const [scenarioResults, setScenarioResults] = useState<Record<string, { success: boolean; excerpt: string }>>({});
  const [error, setError] = useState('');

  const [report, setReport] = useState<VerifyReport | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const abortRef = useRef<AbortController | null>(null);

  // 卸载时中断 SSE 订阅
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const loadReport = useCallback(async (artifactId: string) => {
    try {
      const res = await apiClient.get(`/verify/${artifactId}/report`);
      setReport(res.data.report as VerifyReport);
      setVerifiedAt(res.data.verified_at || null);
    } catch {
      // 404 = 还没有验证报告，按无报告处理
      setReport(null);
      setVerifiedAt(null);
    }
  }, []);

  // 路由参数变化时在渲染期同步输入框（无参时清空产物与报告），替代 effect 内同步 setState
  const [prevRouteId, setPrevRouteId] = useState(routeArtifactId);
  if (prevRouteId !== routeArtifactId) {
    setPrevRouteId(routeArtifactId);
    setIdInput(routeArtifactId || '');
    if (!routeArtifactId) {
      setArtifact(null);
      setReport(null);
    }
  }

  // 路由参数带入产物 ID 时自动加载详情与已有报告；内联 promise 链避免 effect 内同步 setState
  useEffect(() => {
    if (!routeArtifactId) return;
    Promise.resolve()
      .then(() => {
        setLoadingArtifact(true);
        setError('');
        setReport(null);
        return apiClient.get(`/artifacts/${routeArtifactId}`);
      })
      .then(async (res) => {
        const detail = res.data as ArtifactDetail;
        setArtifact(detail);
        const builtinCount = detail.manifest?.validation_scenarios?.length || 0;
        setUseBuiltin(builtinCount > 0);
        setScenarios([emptyScenario()]);
        await loadReport(routeArtifactId);
      })
      .catch((e) => {
        setArtifact(null);
        toast.error('加载产物失败: ' + apiErrorMessage(e));
      })
      .finally(() => setLoadingArtifact(false));
  }, [routeArtifactId, loadReport, toast]);

  const updateScenario = (index: number, patch: Partial<ScenarioRow>) => {
    setScenarios((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const removeScenario = (index: number) => {
    setScenarios((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  /** 组装 scenarios 请求体；useBuiltin 时不传 scenarios 让后端取 manifest。 */
  const buildScenariosPayload = (): { scenarios?: Record<string, unknown>[]; error?: string } => {
    if (useBuiltin) return {};
    const rows = scenarios.filter((s) => s.question.trim());
    if (rows.length === 0) {
      return { error: '请至少填写一个场景问题，或开启"使用产物自带场景"' };
    }
    const payload: Record<string, unknown>[] = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const item: Record<string, unknown> = {
        id: row.id.trim() || `scenario-${i + 1}`,
        question: row.question.trim(),
      };
      if (row.history.trim()) {
        try {
          item.history = JSON.parse(row.history);
        } catch {
          return { error: `场景 #${i + 1} 的 history 不是合法 JSON` };
        }
      }
      payload.push(item);
    }
    return { scenarios: payload };
  };

  const handleEvent = useCallback((event: SseEvent) => {
    const data = (event.data || {}) as Record<string, unknown>;
    switch (event.type) {
      case 'loaded':
        setDoneEvents((prev) => (prev.includes('loaded') ? prev : [...prev, 'loaded']));
        break;
      case 'scenario_done': {
        const sid = String(data.scenario_id || '');
        setScenarioDone((n) => n + 1);
        if (sid) {
          setScenarioResults((prev) => ({
            ...prev,
            [sid]: { success: Boolean(data.success), excerpt: String(data.excerpt || '') },
          }));
        }
        break;
      }
      case 'persisted':
        setDoneEvents((prev) => [...prev, 'persisted']);
        break;
      case 'reported':
        setDoneEvents((prev) => [...prev, 'reported']);
        break;
      case 'done':
        setRunning(false);
        if (data.status === 'succeeded' && data.result) {
          setReport(data.result as VerifyReport);
          setVerifiedAt(new Date().toISOString());
        } else {
          setError(String(data.error || '验证失败'));
        }
        break;
      default:
        break;
    }
  }, []);

  const runVerify = async () => {
    if (!artifact || running) return;
    const { scenarios: payload, error: payloadError } = buildScenariosPayload();
    if (payloadError) {
      toast.error(payloadError);
      return;
    }
    setRunning(true);
    setError('');
    setReport(null);
    setDoneEvents([]);
    setScenarioDone(0);
    setScenarioResults({});
    setScenarioTotal(useBuiltin ? null : (payload?.length ?? 0));
    try {
      const body: Record<string, unknown> = { artifact_id: artifact.id };
      if (payload) body.scenarios = payload;
      const res = await apiClient.post('/verify', body);
      const jobId = res.data.job_id as string;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      readSseEvents(`/api/v1/jobs/${jobId}/events`, handleEvent, controller.signal).catch((e) => {
        if (e?.name === 'AbortError') return;
        setRunning(false);
        setError(e?.message || '订阅任务进度失败');
      });
    } catch (e) {
      setRunning(false);
      toast.error('提交验证失败: ' + apiErrorMessage(e));
    }
  };

  const stopVerify = () => {
    abortRef.current?.abort();
    setRunning(false);
  };

  const toggleExpand = (sid: string) => {
    setExpanded((prev) => ({ ...prev, [sid]: !prev[sid] }));
  };

  const fmtPct = (rate: number | null | undefined) =>
    rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`;

  return (
    <div className="container">
      <h1>
        <FlaskConical size={22} className="inline mr-1" /> 产物试玩面板
      </h1>
      <p className="text-muted mb-4">对能力产物跑一轮最小宿主验证，实时查看验证过程与结构化报告</p>

      {/* 产物选择 */}
      <div className="card mb-4">
        <h3>选择产物</h3>
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            style={{ flex: 1 }}
            placeholder="输入产物 ID（artifact_id），或从素材工坊 / 编辑器跳转带入"
            value={idInput}
            onChange={(e) => setIdInput(e.target.value)}
            aria-label="产物 ID"
          />
          <button
            className="secondary"
            disabled={!idInput.trim() || loadingArtifact}
            onClick={() => navigate(`/playground/${idInput.trim()}`)}
          >
            {loadingArtifact ? <Loader2 size={14} className="inline animate-spin" /> : '加载产物'}
          </button>
        </div>
        {artifact && (
          <div className="flex items-center gap-2 mt-4">
            <strong style={{ fontSize: 16 }}>{artifact.name}</strong>
            <span className="badge secondary">{kindLabel(artifact.kind)}</span>
            <span className="badge secondary text-xs">v{artifact.version}</span>
            <code className="text-muted text-xs">{artifact.id}</code>
          </div>
        )}
      </div>

      {artifact && (
        <>
          {/* 场景编辑 */}
          <div className="card mb-4">
            <h3>验证场景</h3>
            <label className="flex items-center gap-2 mt-2 text-sm" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useBuiltin}
                onChange={(e) => setUseBuiltin(e.target.checked)}
                disabled={running}
              />
              使用产物自带场景（manifest.validation_scenarios）
              {(artifact.manifest?.validation_scenarios?.length || 0) > 0 && (
                <span className="text-muted text-xs">
                  （{artifact.manifest?.validation_scenarios?.length} 个）
                </span>
              )}
            </label>

            {!useBuiltin && (
              <div className="flex flex-col gap-3 mt-4">
                {scenarios.map((row, i) => (
                  <div key={i} className="card" style={{ background: 'var(--bg-tertiary)', padding: 12 }}>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        style={{ width: 160 }}
                        placeholder={`场景 ID（默认 scenario-${i + 1}）`}
                        value={row.id}
                        onChange={(e) => updateScenario(i, { id: e.target.value })}
                        disabled={running}
                        aria-label={`场景 ${i + 1} ID`}
                      />
                      <input
                        type="text"
                        style={{ flex: 1 }}
                        placeholder="场景问题（必填）"
                        value={row.question}
                        onChange={(e) => updateScenario(i, { question: e.target.value })}
                        disabled={running}
                        aria-label={`场景 ${i + 1} 问题`}
                      />
                      <button
                        className="secondary text-xs"
                        onClick={() => removeScenario(i)}
                        disabled={running || scenarios.length <= 1}
                        title="删除场景"
                      >
                        <Trash2 size={12} className="inline text-error" />
                      </button>
                    </div>
                    <input
                      type="text"
                      style={{ width: '100%', marginTop: 8 }}
                      placeholder='历史消息（可选，一行 JSON，如 [{"role":"user","content":"..."}]）'
                      value={row.history}
                      onChange={(e) => updateScenario(i, { history: e.target.value })}
                      disabled={running}
                      aria-label={`场景 ${i + 1} 历史消息`}
                    />
                  </div>
                ))}
                <div className="flex gap-2">
                  <button className="secondary" onClick={() => setScenarios((prev) => [...prev, emptyScenario()])} disabled={running}>
                    <Plus size={14} className="inline mr-1" /> 添加场景
                  </button>
                  <button
                    className="secondary"
                    onClick={() => setScenarios((prev) => [...prev, sampleScenario(artifact.kind)])}
                    disabled={running}
                  >
                    <Sparkles size={14} className="inline mr-1" /> 添加示例场景
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button className="primary" onClick={runVerify} disabled={running}>
                {running ? (
                  <>
                    <Loader2 size={14} className="inline animate-spin mr-1" /> 验证中...
                  </>
                ) : report ? (
                  <>
                    <RotateCcw size={14} className="inline mr-1" /> 再验证
                  </>
                ) : (
                  <>
                    <Play size={14} className="inline mr-1" /> 开始验证
                  </>
                )}
              </button>
              {running && (
                <button className="secondary" onClick={stopVerify}>
                  <Square size={14} className="inline mr-1" /> 中断
                </button>
              )}
            </div>
          </div>

          {/* 运行进度 */}
          {(running || doneEvents.length > 0 || error) && (
            <div className="card mb-4">
              <h3>验证进度</h3>
              <div className="list mt-4">
                {STAGES.map((stage) => {
                  const done =
                    stage.key === 'scenarios' ? doneEvents.includes('persisted') : doneEvents.includes(stage.key);
                  const started = doneEvents.length > 0 || scenarioDone > 0;
                  const isCurrent =
                    !done && !error && started && STAGES.findIndex((s) => {
                      if (s.key === 'scenarios') return !doneEvents.includes('persisted');
                      return !doneEvents.includes(s.key);
                    }) === STAGES.indexOf(stage);
                  return (
                    <div key={stage.key} className="list-item">
                      <div className="flex items-center gap-2">
                        {done ? (
                          <CheckCircle2 size={16} style={{ color: 'var(--color-success, #22c55e)' }} />
                        ) : isCurrent && running ? (
                          <Loader2 size={16} className="animate-spin text-primary" />
                        ) : (
                          <Circle size={16} className="text-muted" />
                        )}
                        <span style={{ color: done || isCurrent ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {stage.label}
                          {stage.key === 'scenarios' && scenarioDone > 0 && (
                            <span className="text-muted text-sm">
                              （已完成 {scenarioDone}
                              {scenarioTotal !== null ? `/${scenarioTotal}` : ''} 个场景）
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {Object.keys(scenarioResults).length > 0 && (
                <div className="mt-4">
                  <div className="text-sm text-muted mb-1">场景执行结果</div>
                  <div className="list">
                    {Object.entries(scenarioResults).map(([sid, r]) => (
                      <div key={sid} className="list-item">
                        <div className="flex items-center gap-2">
                          {r.success ? (
                            <CheckCircle2 size={14} style={{ color: 'var(--color-success, #22c55e)' }} />
                          ) : (
                            <XCircle size={14} className="text-error" />
                          )}
                          <code className="text-sm">{sid}</code>
                          {r.excerpt && <span className="text-muted text-xs">{r.excerpt}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="card mt-4" style={{ borderColor: 'var(--color-error, #ef4444)' }}>
                  <div className="flex items-center gap-2">
                    <XCircle size={16} className="text-error" />
                    <strong className="text-error">验证失败</strong>
                  </div>
                  <p className="text-muted text-sm mt-2">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* 验证报告 */}
          {report && (
            <div className="card mb-4">
              <h3>验证报告</h3>
              {verifiedAt && <div className="text-muted text-xs mt-1">验证时间：{verifiedAt}</div>}

              <div className="flex items-center gap-4 mt-4" style={{ flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 40, fontWeight: 700, lineHeight: 1.1 }}>
                    {fmtPct(report.pass_rate)}
                  </div>
                  <div className="text-muted text-sm">
                    通过率（{report.scenarios_passed}/{report.scenarios_total} 个场景）
                  </div>
                </div>
                <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, flex: 1 }}>
                  <div className="card" style={{ background: 'var(--bg-tertiary)', padding: 12 }}>
                    <div className="text-sm text-muted">触发</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{fmtPct(report.dimensions.trigger.rate)}</div>
                    <div className="text-muted text-xs">
                      {report.dimensions.trigger.scenarios_triggered} 个场景触发 skill
                    </div>
                  </div>
                  <div className="card" style={{ background: 'var(--bg-tertiary)', padding: 12 }}>
                    <div className="text-sm text-muted">引用</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{report.dimensions.reference.citation_markers}</div>
                    <div className="text-muted text-xs">
                      引用标记；{report.dimensions.reference.reference_reads} 次资料读取
                    </div>
                  </div>
                  <div className="card" style={{ background: 'var(--bg-tertiary)', padding: 12 }}>
                    <div className="text-sm text-muted">工具</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{fmtPct(report.dimensions.tool.success_rate)}</div>
                    <div className="text-muted text-xs">
                      {report.dimensions.tool.calls} 次调用，{report.dimensions.tool.failed} 次失败
                    </div>
                  </div>
                  <div className="card" style={{ background: 'var(--bg-tertiary)', padding: 12 }}>
                    <div className="text-sm text-muted">边界</div>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{report.dimensions.boundary.scenarios_rejected}</div>
                    <div className="text-muted text-xs">个场景按约束拒答</div>
                  </div>
                </div>
              </div>

              {/* 弱项列表 + 去改写 */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-1">
                  <strong className="text-sm">弱项（{report.weaknesses.length}）</strong>
                  <Link to={`/artifacts/${artifact.id}/edit`} className="text-sm">
                    <PenLine size={12} className="inline mr-1" /> 去改写
                  </Link>
                </div>
                {report.weaknesses.length === 0 ? (
                  <div className="text-muted text-sm">没有发现弱项</div>
                ) : (
                  <div className="list">
                    {report.weaknesses.map((w, i) => (
                      <div key={i} className="list-item">
                        <div className="flex items-center gap-2">
                          <span className="badge error text-xs">{weaknessLabel(w.type)}</span>
                          <code className="text-sm">{w.scenario_id}</code>
                          <span className="text-muted text-sm">{w.detail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 场景明细（可展开） */}
              <div className="mt-4">
                <strong className="text-sm">场景明细</strong>
                <div className="list mt-2">
                  {report.scenarios.map((s) => {
                    const open = Boolean(expanded[s.scenario_id]);
                    return (
                      <div key={s.scenario_id} className="list-item" style={{ display: 'block' }}>
                        <button
                          className="flex items-center gap-2"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', padding: 0 }}
                          onClick={() => toggleExpand(s.scenario_id)}
                        >
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          {s.success ? (
                            <CheckCircle2 size={14} style={{ color: 'var(--color-success, #22c55e)' }} />
                          ) : (
                            <XCircle size={14} className="text-error" />
                          )}
                          <code className="text-sm">{s.scenario_id}</code>
                          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{s.question}</span>
                        </button>
                        {open && (
                          <div className="mt-2 text-sm" style={{ paddingLeft: 22 }}>
                            <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                              <span className="badge secondary text-xs">{s.skill_triggered ? '已触发 skill' : '未触发 skill'}</span>
                              <span className="badge secondary text-xs">资料读取 {s.reference_reads}</span>
                              <span className="badge secondary text-xs">引用命中 {s.citation_hits}</span>
                              <span className="badge secondary text-xs">
                                工具调用 {s.tool_calls}（失败 {s.tool_failures}）
                              </span>
                              {s.refused && <span className="badge secondary text-xs">按约束拒答</span>}
                            </div>
                            {s.final_excerpt && (
                              <p className="text-muted mt-2" style={{ margin: 0 }}>
                                回答摘要：{s.final_excerpt}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
